import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Tag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/motion";
import { getOffers, rupee } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const { addItem, setOpen } = useCart();

  useEffect(() => {
    getOffers().then((d) => setOffers(d.offers));
  }, []);

  const addCombo = (o) => {
    addItem({ id: o.id, title: o.title, price: o.price }, "offer");
    toast.success(`${o.title} added to cart`);
    setOpen(true);
  };

  return (
    <div data-testid="offers-page" className="pb-32">
      <PageHero
        testid="offers-hero"
        eyebrow="Combos that hit different"
        title="Exclusive"
        accent="Offers"
        subtitle="Bundled up, priced down. From solo cravings to full-squad feasts — grab a box and save."
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-14 grid md:grid-cols-2 gap-6">
        {offers.map((o, i) => {
          const save = o.mrp - o.price;
          const isYellow = o.color === "yellow";
          return (
            <Reveal key={o.id} delay={i * 0.08}>
              <div
                data-testid={`offer-card-${o.id}`}
                className={`relative h-full rounded-lg border p-7 overflow-hidden ${
                  isYellow ? "bg-chick text-brown border-chick" : "bg-card border-border"
                }`}
              >
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-orange/20 blur-2xl" aria-hidden="true" />
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full ${isYellow ? "bg-brown text-chick" : "bg-orange text-primary-foreground"}`}>
                    {o.tag}
                  </span>
                  <span className={`flex items-center gap-1 font-bold text-sm ${isYellow ? "text-brown" : "text-orange"}`}>
                    <Tag className="w-4 h-4" /> Save {rupee(save)}
                  </span>
                </div>

                <h3 className={`font-heading uppercase text-4xl sm:text-5xl tracking-tighter leading-none mt-5 ${isYellow ? "text-brown" : "text-cream"}`}>
                  {o.title}
                </h3>
                <p className={`mt-3 ${isYellow ? "text-brown/80" : "text-cream/70"}`}>{o.desc}</p>

                <ul className="mt-5 space-y-1.5">
                  {o.items.map((it) => (
                    <li key={it} className={`flex items-center gap-2 text-sm ${isYellow ? "text-brown/90" : "text-cream/80"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isYellow ? "bg-brown" : "bg-orange"}`} /> {it}
                    </li>
                  ))}
                </ul>

                <div className="flex items-end justify-between mt-7">
                  <div>
                    <span className={`line-through text-sm ${isYellow ? "text-brown/50" : "text-cream/40"}`}>{rupee(o.mrp)}</span>
                    <p className={`font-heading text-4xl ${isYellow ? "text-brown" : "text-orange"}`}>{rupee(o.price)}</p>
                  </div>
                  <button
                    data-testid={`offer-add-${o.id}`}
                    onClick={() => addCombo(o)}
                    className={`flex items-center gap-2 font-bold uppercase text-sm px-6 py-3.5 rounded-md hover:scale-105 transition-transform ${
                      isYellow ? "bg-brown text-chick" : "bg-orange text-primary-foreground"
                    }`}
                  >
                    Add Combo <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-16"
      >
        <div className="rounded-lg border border-dashed border-orange/50 p-8 text-center">
          <p className="font-accent text-chick text-2xl">Psst… first order?</p>
          <p className="text-cream/80 text-lg mt-1">Use code <span className="font-heading text-orange text-2xl">KUKKAD10</span> for 10% off your first direct order on WhatsApp.</p>
        </div>
      </motion.div>
    </div>
  );
}
